/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect, { SelectChangeEvent } from '@mui/material/Select';
import styled from 'styled-components';

import { Box, Checkbox, Chip, FormControl, ListItemText, alpha } from '@mui/material';
import { Option } from '@common/BucketInput/BucketField';

export const SelectContainer = styled.div`
  width: 100%;

  & .MuiChip-root {
    max-height: 23px;
    font-size: 1.5ch;
  }

  & .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.line.light} !important;
  }

  & .MuiInputBase-root {
    min-height: 40px !important;
  }

  & .MuiOutlinedInput-input {
    padding: 8.5px 13px !important;
  }

  & .Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.primary} !important;
  }

  & .MuiSelect-select {
    font-size: 14px;
  }

  & .MuiFormControl-root {
    min-height: 40px !important;
  }
`;

const Placeholder = styled.div`
  color: ${({ theme }) => alpha(theme.text.standard, 0.4)};
  font-family: 'Inter', Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
`;

interface Props {
  value: string;
  setValue?: React.Dispatch<string>;
  onChange?: (event: SelectChangeEvent<string>) => void;
  placeholder?: string;
  options: Option[];
  allOptions?: Option[];
  maxWidth?: string;
  disabled?: boolean;
}

export const Select: React.FC<Props> = ({
  value,
  setValue,
  onChange,
  options,
  allOptions = options,
  placeholder,
  disabled,
}) => {
  const handleChange = (event: any): void => {
    if (setValue === undefined) return;
    if (value === event.target.value) setValue('');
    else setValue(event.target.value);
  };

  return (
    <SelectContainer>
      <FormControl fullWidth>
        <MuiSelect
          sx={{ '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
          value={value || 'N/A'}
          size='small'
          displayEmpty
          disabled={disabled}
          onChange={onChange ?? handleChange}
          renderValue={(selected) => {
            if (value?.length === 0) return <Placeholder>{placeholder}</Placeholder>;

            const option = allOptions.find((x) => x.value === selected) as Option;
            return option?.label;
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>
      </FormControl>
    </SelectContainer>
  );
};

interface PropsMulti {
  value: string[];
  setValue?: React.Dispatch<string[]>;
  onChange?: (event: SelectChangeEvent<string[]>) => void;
  placeholder?: string;
  options: Option[];
  allOptions?: Option[];
  maxWidth?: string;
  disabled?: boolean;
}

const MultiMenuItem = styled(MenuItem)<{ multiple: boolean }>``;

export const MultiSelect: React.FC<PropsMulti> = ({ value, setValue, onChange, options, disabled, placeholder }) => {
  const handleChange = (event: any): void => {
    const {
      target: { value },
    } = event;

    if (setValue === undefined) return;
    if (value === event.target.value) setValue(typeof value === 'string' ? value.split(',') : value);
    else setValue(event.target.value);
  };

  return (
    <SelectContainer>
      <FormControl fullWidth>
        <MuiSelect
          sx={{ '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
          value={value || 'N/A'}
          size='small'
          displayEmpty
          multiple
          disabled={disabled}
          onChange={onChange ?? handleChange}
          renderValue={(selected) => {
            if (value?.length === 0) return <Placeholder>{placeholder}</Placeholder>;

            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip size='small' key={value} label={value} />
                ))}
              </Box>
            );
          }}
        >
          {options.map((option) => (
            <MultiMenuItem key={option.value} value={option.value} multiple>
              <Checkbox checked={value?.includes(option.label)} size='small' />
              <ListItemText primary={option.label} />
            </MultiMenuItem>
          ))}
        </MuiSelect>
      </FormControl>
    </SelectContainer>
  );
};
